import React from 'react'
import { Menu } from 'lucide-react'
import { Sidebar } from './Sidebar'
import { ImpersonationBanner } from './ImpersonationBanner'
import { NavUser } from './NavUser'
import { ThemeToggle } from './ThemeToggle'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'

interface AppShellProps {
  children: React.ReactNode
  user: { email: string; role: string }
  organization: { name: string; slug: string }
}

export function AppShell({ children, user, organization }: AppShellProps) {
  return (
    <div
      className="relative flex min-h-screen"
      style={{ background: 'hsl(var(--surface-sunken))' }}
    >
      {/* Ambient grid backplate — replaces hard side border */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 engineer-grid opacity-30"
        style={{
          maskImage:
            'radial-gradient(ellipse at top, black 0%, transparent 75%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at top, black 0%, transparent 75%)',
        }}
      />

      {/* Desktop sidebar — surface column, no thin border */}
      <aside
        className="hidden md:flex w-64 shrink-0 flex-col"
        style={{ background: 'hsl(var(--sidebar))' }}
      >
        <Sidebar orgName={organization.name} />
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col">
        {/* Topbar — soft shadow band, no border-b */}
        <header
          className="sticky top-0 z-30 flex h-16 items-center gap-4 px-6"
          style={{
            background:
              'linear-gradient(180deg, hsl(var(--background) / 0.85) 0%, hsl(var(--background) / 0.7) 100%)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            boxShadow:
              '0 1px 0 hsl(var(--foreground) / 0.04), 0 6px 24px -12px hsl(var(--foreground) / 0.1)',
          }}
        >
          {/* Mobile hamburger */}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <Sidebar orgName={organization.name} />
            </SheetContent>
          </Sheet>

          {/* Org name for mobile */}
          <span className="font-heading text-sm font-semibold tracking-tight md:hidden">
            {organization.name}
          </span>

          {/* Live status pill — visible reassurance, replaces the old vertical separator */}
          <div className="hidden md:flex items-center gap-2 rounded-md border border-foreground/[0.06] bg-background/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <span className="live-dot" />
            api · operational
          </div>

          <div className="flex flex-1 items-center justify-end gap-2">
            <ThemeToggle />
            <NavUser email={user.email} />
          </div>
        </header>

        {/* Impersonation banner */}
        <ImpersonationBanner />

        {/* Page content */}
        <main className="flex-1 overflow-auto px-4 py-6 sm:px-8 sm:py-8">
          <div className="mx-auto max-w-7xl">{children}</div>
        </main>
      </div>
    </div>
  )
}
